import React from 'react';
import { Link } from 'react-router-dom';
import { Stethoscope, ArrowLeft, FileText, AlertTriangle, Shield, Users, CreditCard, Globe } from 'lucide-react';

const Section = ({ id, icon: Icon, title, children }) => (
  <section id={id} className="scroll-mt-20">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#ffdea8' }}>
        <Icon className="w-4 h-4" style={{ color: '#5f4300' }} />
      </div>
      <h2 className="font-bold text-lg" style={{ color: '#191c1e' }}>{title}</h2>
    </div>
    <div className="text-sm leading-relaxed space-y-3 pl-12" style={{ color: '#424752' }}>
      {children}
    </div>
  </section>
);

const TermsPage = () => {
  const toc = [
    { id: 'acceptance', label: 'Acceptance of Terms' },
    { id: 'service', label: 'The Platform' },
    { id: 'accounts', label: 'Account Terms' },
    { id: 'medical', label: 'Medical Disclaimer' },
    { id: 'clinicians', label: 'Clinician Responsibilities' },
    { id: 'privacy', label: 'Privacy & Data' },
    { id: 'ip', label: 'Intellectual Property' },
    { id: 'liability', label: 'Limitation of Liability' },
    { id: 'termination', label: 'Termination' },
    { id: 'governing', label: 'Governing Law' },
  ];

  return (
    <div className="min-h-screen font-sans" style={{ background: '#f9f9fd', color: '#191c1e' }}>
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#00478d,#005eb8)' }}>
              <Stethoscope className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="font-bold text-base tracking-tight" style={{ color: '#191c1e' }}>
              Sympto<span style={{ color: '#00478d' }}>Connect</span>
            </span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm font-semibold hover:text-[#00478d] transition-colors" style={{ color: '#424752' }}>
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">

          {/* Sidebar TOC */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <p className="text-xs font-bold uppercase tracking-wider mb-4" style={{ color: '#727783' }}>On this page</p>
              <nav className="space-y-1">
                {toc.map(t => (
                  <a
                    key={t.id}
                    href={`#${t.id}`}
                    className="block text-sm py-1.5 px-3 rounded-lg transition-colors hover:bg-white hover:text-[#00478d]"
                    style={{ color: '#424752' }}
                  >
                    {t.label}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <main className="lg:col-span-3 space-y-12">
            {/* Header */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-5" style={{ background: '#ffdea8', color: '#5f4300' }}>
                <FileText className="w-3.5 h-3.5" /> Terms of Service
              </div>
              <h1 className="font-extrabold mb-3" style={{ fontSize: 'clamp(1.75rem,4vw,2.5rem)', letterSpacing: '-0.02em', color: '#191c1e' }}>
                Terms of Service
              </h1>
              <p className="text-base leading-relaxed" style={{ color: '#424752' }}>
                Please read these terms carefully before using SymptoConnect. By creating an account or accessing the platform, you agree to be bound by these Terms of Service and our Privacy Policy.
              </p>
              <p className="text-xs mt-4 font-semibold" style={{ color: '#727783' }}>Effective date: January 1, 2026 · Last updated: April 11, 2026</p>
            </div>

            <Section id="acceptance" icon={FileText} title="Acceptance of Terms">
              <p>By accessing or using SymptoConnect (the "Platform"), you agree to these Terms of Service ("Terms"), our <Link to="/privacy" className="underline" style={{ color: '#00478d' }}>Privacy Policy</Link>, and all applicable laws and regulations.</p>
              <p>If you do not agree to these Terms, you may not access or use the Platform. These Terms constitute a legally binding agreement between you and SymptoConnect, Inc. ("SymptoConnect," "we," "our," or "us").</p>
              <p>We reserve the right to modify these Terms at any time. Material changes will be communicated via email and/or an in-app notice at least 14 days before taking effect. Continued use of the Platform after changes constitutes acceptance.</p>
            </Section>

            <Section id="service" icon={FileText} title="The Platform">
              <p>SymptoConnect provides a digital health coordination platform enabling patients to log symptoms, track health data, and communicate with authorized healthcare providers. The Platform includes:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Symptom and health data logging tools for patients</li>
                <li>AI-powered risk assessment and care recommendations</li>
                <li>Secure messaging between patients and their care teams</li>
                <li>A clinical dashboard for authorized healthcare providers</li>
                <li>Data export and transparency tools for patients</li>
              </ul>
              <p>We reserve the right to modify, suspend, or discontinue any part of the Platform at any time with reasonable notice.</p>
            </Section>

            <Section id="accounts" icon={Users} title="Account Terms">
              <p><strong style={{ color: '#191c1e' }}>Eligibility.</strong> You must be at least 18 years old to create an account, or have parental/guardian consent if between 13–17. The Platform is not intended for children under 13.</p>
              <p><strong style={{ color: '#191c1e' }}>Account security.</strong> You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. Notify us immediately at <a href="mailto:security@symptoconnect.com" className="underline" style={{ color: '#00478d' }}>security@symptoconnect.com</a> if you suspect unauthorized access.</p>
              <p><strong style={{ color: '#191c1e' }}>Accurate information.</strong> You agree to provide accurate, current, and complete information when creating and maintaining your account. Providing false health or identity information may result in account suspension.</p>
              <p><strong style={{ color: '#191c1e' }}>One account per person.</strong> You may not create multiple accounts or share your account with others.</p>
            </Section>

            <Section id="medical" icon={AlertTriangle} title="Medical Disclaimer">
              <div className="p-4 rounded-xl border-l-4" style={{ background: '#fff8e7', borderColor: '#ffba20' }}>
                <p className="font-bold mb-2" style={{ color: '#5f4300' }}>Important — Please read carefully.</p>
                <p style={{ color: '#5f4300' }}>SymptoConnect is a health coordination and monitoring tool — <strong>not a medical device, diagnostic tool, or replacement for professional medical care.</strong></p>
              </div>
              <p>The Platform:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Does not provide medical diagnoses or treatment recommendations</li>
                <li>Is not a substitute for consultation with a qualified healthcare professional</li>
                <li>Is not intended to be used in medical emergencies</li>
                <li>Should not be used as the sole basis for any medical decision</li>
              </ul>
              <p><strong style={{ color: '#191c1e' }}>In a medical emergency, call 911 or your local emergency number immediately.</strong></p>
              <p>AI-generated risk scores and care suggestions are decision-support tools for licensed clinicians. They do not constitute clinical advice and require professional clinical judgment to interpret and act upon.</p>
            </Section>

            <Section id="clinicians" icon={Shield} title="Clinician Responsibilities">
              <p>Healthcare providers using SymptoConnect agree to:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Maintain a valid license to practice in their jurisdiction throughout their use of the Platform</li>
                <li>Only access patient data for which they have valid patient consent or a HIPAA-recognized legal basis</li>
                <li>Not use AI-generated risk scores or recommendations as a substitute for clinical judgment</li>
                <li>Comply with all applicable medical, privacy, and professional ethics laws</li>
                <li>Promptly report any suspected data breach or unauthorized access to patient information</li>
              </ul>
              <p>Clinicians accessing the platform during verification are subject to additional terms in the Clinician Agreement provided during onboarding.</p>
            </Section>

            <Section id="privacy" icon={Shield} title="Privacy & Data">
              <p>Your use of the Platform is also governed by our <Link to="/privacy" className="underline" style={{ color: '#00478d' }}>Privacy Policy</Link>, which is incorporated into these Terms by reference.</p>
              <p>You retain ownership of all health data you submit to the Platform. By submitting data, you grant SymptoConnect a limited license to process and store that data to provide the service, as described in the Privacy Policy.</p>
              <p>You may export or delete your data at any time through the My Data & Privacy section of your account.</p>
            </Section>

            <Section id="ip" icon={FileText} title="Intellectual Property">
              <p>The SymptoConnect platform, including its design, software, algorithms, and content (excluding your personal health data), is owned by SymptoConnect, Inc. and protected by copyright, trademark, and other intellectual property laws.</p>
              <p>You may not copy, modify, distribute, sell, or reverse-engineer any part of the Platform without our express written consent.</p>
            </Section>

            <Section id="liability" icon={AlertTriangle} title="Limitation of Liability">
              <p>To the maximum extent permitted by law, SymptoConnect and its officers, employees, and affiliates shall not be liable for:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                <li>Loss of data, revenue, goodwill, or health outcomes arising from use or inability to use the Platform</li>
                <li>Actions or omissions of healthcare providers using the Platform</li>
                <li>Inaccuracies in AI-generated risk scores or recommendations</li>
              </ul>
              <p>Our total liability for any claim arising from these Terms or the Platform shall not exceed the amounts you paid to SymptoConnect in the 12 months preceding the claim.</p>
            </Section>

            <Section id="termination" icon={AlertTriangle} title="Termination">
              <p><strong style={{ color: '#191c1e' }}>By you.</strong> You may close your account at any time through Settings → Account. Upon closure, your personal data will be deleted as described in our Privacy Policy.</p>
              <p><strong style={{ color: '#191c1e' }}>By us.</strong> We may suspend or terminate your access immediately if we determine that you have violated these Terms, engaged in fraudulent activity, or posed a risk to other users or the Platform.</p>
              <p>Upon termination, your right to access the Platform ceases. Sections on Medical Disclaimer, Limitation of Liability, and Governing Law survive termination.</p>
            </Section>

            <Section id="governing" icon={Globe} title="Governing Law">
              <p>These Terms are governed by the laws of the State of California, United States, without regard to conflict of law provisions.</p>
              <p>Any disputes arising from these Terms or your use of the Platform will be resolved through binding arbitration in San Francisco County, California, except that either party may seek injunctive relief in court for intellectual property infringement.</p>
              <p>For questions about these Terms, contact us at <a href="mailto:legal@symptoconnect.com" className="underline" style={{ color: '#00478d' }}>legal@symptoconnect.com</a>.</p>
            </Section>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8" style={{ background: '#191c1e', color: 'rgba(255,255,255,0.5)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <p>© 2026 SymptoConnect. All rights reserved.</p>
          <div className="flex gap-5">
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
